import { Injectable } from '@angular/core';
import { Subscription, timer } from 'rxjs';
import { OeAppService } from '../../../shared/oe-app.service';
import { ServerTimeService } from '../../../shared/services/server-time/server-time.service';
import { LiveClassEventDto } from '../../live-class-event-dto';
import { LiveClassEventService } from '../../live-class-event.service';
import { getRandomDelay } from './live-class-helpers';

@Injectable({
  providedIn: 'root',
})
export class LiveClassCountdownTimerService {
  public labelKey: string;
  public msToCountdown: number;
  public currentSignUpOffset: number;

  private subscriptions = new Subscription();
  private classBegin: number;
  private signUpEnd: number;
  private signUpStart: number;
  private waitingForNeedsNewClassesTimeout: boolean = undefined;

  constructor(
    oeApp: OeAppService,
    private serverTime: ServerTimeService,
    private liveClassEventService: LiveClassEventService
  ) {
    this.signUpStart = oeApp.getData('liveClassStudentSignUpStart');
    this.signUpEnd = oeApp.getData('liveClassStudentSignUpEnd');

    this.subscriptions.add(timer(1, 1000).subscribe(() => this.update()));

    this.serverTime.updateServerTimeOffset();

    this.subscribeToLiveClassEventOnChange();
  }

  public destroy() {
    this.subscriptions.unsubscribe();
  }

  private subscribeToLiveClassEventOnChange() {
    this.subscriptions.add(
      this.liveClassEventService.onChange.subscribe((event: LiveClassEventDto) => {
        if (!event) {
          return;
        }
        switch (event.event) {
          case 'WAITING_FOR_FETCH_NEXT_LIVE_CLASSES':
            this.onWaitingForFetchNextLiveClassesEvent();
            break;
          case 'WAITING_FOR_SIGNUP':
            this.onWaitingForSignupEvent(event.data);
            break;
          case 'CAN_SIGN_UP':
            this.onCanSignUpEvent(event.data);
            break;
          case 'HAS_JOIN':
          case 'HAS_REJOIN':
            break;
        }
      })
    );
  }

  private update(): void {
    if (!this.classBegin) {
      return;
    }
    const msToClassBegin = this.classBegin - this.serverTime.offsetNow();
    if (msToClassBegin < this.signUpEnd) {
      // We're at minute 27th or 57th
      if (this.waitingForNeedsNewClassesTimeout === undefined) {
        // It's undefined if the user opened the page when we're at minute 27th or 57th
        this.triggerNextLiveClassEvent();
      } else {
        if (!this.waitingForNeedsNewClassesTimeout) {
          this.liveClassEventService.triggerEvent('WAITING_FOR_FETCH_NEXT_LIVE_CLASSES', null);
        }
      }
    } else {
      this.waitingForNeedsNewClassesTimeout = false;
      if (msToClassBegin < this.signUpStart) {
        this.currentSignUpOffset = this.signUpEnd;
        const msToSignupEnd = msToClassBegin - this.signUpEnd;
        this.liveClassEventService.triggerEvent('CAN_SIGN_UP', msToSignupEnd);
      } else {
        this.currentSignUpOffset = this.signUpStart;
        const msToSignupStart = msToClassBegin - this.signUpStart;
        this.liveClassEventService.triggerEvent('WAITING_FOR_SIGNUP', msToSignupStart);
      }
    }
  }

  private triggerNextLiveClassEvent(): void {
    this.serverTime.updateServerTimeOffset();
    this.waitingForNeedsNewClassesTimeout = false;
    this.liveClassEventService.triggerEvent('NEEDS_NEW_CLASSES', null);
  }

  private onWaitingForFetchNextLiveClassesEvent(): void {
    this.waitingForNeedsNewClassesTimeout = true;
    this.labelKey = 'studybundle.liveClasses.nextLiveClassWaitingMessage';
    const randomDelay = getRandomDelay();
    console.log(new Date(), 'Next live classes will be fetched in', Math.round(randomDelay / 1000), 'seconds');
    setTimeout(() => this.triggerNextLiveClassEvent(), randomDelay);
  }

  private onWaitingForSignupEvent(msToSignupStart: number): void {
    this.labelKey = 'studybundle.liveClasses.nextLiveClassMessage';
    this.msToCountdown = msToSignupStart;
  }

  private onCanSignUpEvent(msToSignupEnd: number): void {
    this.labelKey = 'studybundle.liveClasses.signUpEndMessage';
    this.msToCountdown = msToSignupEnd;
  }

  private pad(digit: number): string {
    return digit <= 9 ? `0${digit}` : digit.toString();
  }

  public seconds(): string {
    if (this.msToCountdown && this.msToCountdown > 0) {
      return this.pad(Math.floor(this.msToCountdown / 1000) % 60);
    } else {
      return '00';
    }
  }

  public minutes(signupOffset = 0): string {
    if (this.msToCountdown && this.msToCountdown > 0) {
      return this.pad((Math.floor(this.msToCountdown / (1000 * 60)) % 60) + signupOffset);
    } else {
      return '00';
    }
  }

  public setClassBegin(value: number) {
    this.classBegin = value;
  }
}



// WEBPACK FOOTER //
// ./ts/app/home/live-class-section/services/live-class-countdown-timer.service.ts